import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
  ItemReorderEventDetail,
  ModalController,
  ViewWillEnter,
} from '@ionic/angular';
import { map, ReplaySubject, takeUntil } from 'rxjs';

import { BadgesService } from '../../../../services/badges.service';
import { CategoriesService } from '../../../../services/categories.service';
import { ConfirmationPage } from '../../../modals/confirmation/confirmation.page';
import { ErrorPage } from '../../../modals/error/error.page';
import { PromptPage } from '../../../modals/prompt/prompt.page';

interface AdminViewModel {
  categories: {
    id: string;
    name: string;
    badges: {
      id: string;
      icon: string;
      name: string;
      description: string;
    }[];
  }[];
}

@Component({
  selector: 'app-admin',
  templateUrl: './admin.page.html',
  styleUrls: ['./admin.page.scss'],
})
export class AdminPage implements ViewWillEnter, OnDestroy {
  viewModel: AdminViewModel;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private badgesService: BadgesService,
    private categoriesService: CategoriesService,
    private modalController: ModalController,
    private router: Router
  ) {}

  ionViewWillEnter(): void {
    this.loadCategories();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  mockLoadingItems(count: number) {
    return new Array(count);
  }

  recordTrackBy(index: number, record: any) {
    return record?.id ?? index;
  }

  handleReorder(ev: CustomEvent<ItemReorderEventDetail>) {
    const { from, to, complete } = ev.detail;

    this.categoriesService
      .reorderCategory(from, to, {
        ignoreErrors: true,
      })
      .subscribe({
        next: async () => {
          complete(true);

          await this.loadCategories();
        },
        error: () => {
          this.presentErrorModal(
            'Ocorreu um erro ao reordenar os registros. Por favor, tente novamente mais tarde.'
          );

          complete(false);
        },
      });
  }

  async handleCategoryUpsert(category?: any) {
    const modal = await this.modalController.create({
      component: PromptPage,
      cssClass: 'app-auto-height-modal',
      componentProps: {
        title: category ? 'Editar nome da seção' : 'Nova seção',
        placeholder: 'Digite o nome da seção',
        value: category?.name,
        negativeButtonText: 'Cancelar',
        positiveButtonText: 'Salvar',
      },
    });

    await modal.present();

    const { data: name } = await modal.onWillDismiss();
    if (name) {
      const record = {
        ...category,
        name,
      };

      this.categoriesService
        .upsertCategory(record, {
          ignoreErrors: true,
        })
        .subscribe({
          next: () => this.loadCategories(),
          error: () =>
            this.presentErrorModal(
              'Ocorreu um erro ao salvar o registro. Por favor, tente novamente mais tarde.'
            ),
        });
    }
  }

  async handleCategoryDelete(category: any) {
    const modal = await this.modalController.create({
      component: ConfirmationPage,
      cssClass: 'app-auto-height-modal',
      componentProps: {
        icon: '../../../assets/icons/delete.svg',
        color: 'danger',
        title: 'Apagar seção',
        description: `Você tem certeza que deseja apagar a seção "${category.name}"? As medalhas serão apagadas e essa ação não pode ser desfeita.`,
        negativeButtonText: 'Cancelar',
        positiveButtonText: 'Excluir',
      },
    });

    await modal.present();

    const { data: confirmed } = await modal.onWillDismiss();
    if (confirmed) {
      this.categoriesService
        .deleteCategory(category.id, {
          ignoreErrors: true,
        })
        .subscribe({
          next: () => this.loadCategories(),
          error: ({ error }) =>
            this.presentErrorModal(
              error?.message ??
                'Ocorreu um erro ao excluir o registro. Por favor, tente novamente mais tarde.'
            ),
        });
    }
  }

  async handleBadgeAdd(category: any) {
    await this.router.navigate(['/badges/admin/edit/new'], {
      queryParams: {
        categoryId: category.id,
      },
    });
  }

  async handleBadgeEdit(badge: any) {
    await this.router.navigate(['/badges/admin/edit', badge.id]);
  }

  async handleBadgeDelete(badge: any) {
    const modal = await this.modalController.create({
      component: ConfirmationPage,
      cssClass: 'app-auto-height-modal',
      componentProps: {
        icon: '../../../assets/icons/delete.svg',
        color: 'danger',
        title: 'Apagar medalha',
        description:
          'Você deseja apagar a medalha? Essa ação não pode ser desfeita.',
        negativeButtonText: 'Cancelar',
        positiveButtonText: 'Excluir',
      },
    });

    await modal.present();

    const { data: confirmed } = await modal.onWillDismiss();
    if (confirmed) {
      this.badgesService
        .deleteBadge(badge.id, {
          ignoreErrors: true,
        })
        .subscribe({
          next: () => this.loadCategories(),
          error: ({ error }) =>
            this.presentErrorModal(
              error?.message ??
                'Ocorreu um erro ao excluir o registro. Por favor, tente novamente mais tarde.'
            ),
        });
    }
  }

  private async loadCategories(): Promise<void> {
    return new Promise<void>((resolve, reject) =>
      this.categoriesService
        .getCategories(false, {
          invalidateCache: true,
          ignoreErrors: true,
        })
        .pipe(
          takeUntil(this.destroyed$),
          map((categories) => ({
            categories: categories.map((category) => ({
              id: category.id,
              name: category.name,
              order: category.order,
              badges: category.badges.map(
                ({ id, iconColored, name, description }) => ({
                  id,
                  icon: iconColored,
                  name,
                  description,
                })
              ),
            })),
          }))
        )
        .subscribe({
          next: (viewModel) => {
            this.viewModel = viewModel;
            resolve();
          },
          error: reject,
        })
    );
  }

  private async presentErrorModal(message: string) {
    const modal = await this.modalController.create({
      component: ErrorPage,
      cssClass: 'app-auto-height-modal',
      componentProps: {
        message,
      },
    });

    await modal.present();
  }
}
